import axios from 'axios'

export interface Reservation {
  reservation_id: string
  user_id: string
  merchant_id: string
  basket_id: string | null
  product_id: string | null
  offer_id: string | null
  quantity: number
  total_amount: number | string
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed'
  pickup_time: string | null
  scheduled_at: string | null
  qr_code_data: string | null
  qr_code_expires_at: string | null
  created_at: string
  updated_at: string
  user_first_name: string | null
  user_last_name: string | null
  merchant_name: string | null
  notes?: string
}

export interface CreateReservationDto {
  user_id: string
  merchant_id: string
  basket_id?: string
  product_id?: string
  offer_id?: string
  quantity: number
  total_amount: number
  pickup_time?: string
}

export interface UpdateReservationDto {
  basket_id?: string
  product_id?: string
  offer_id?: string
  quantity?: number
  total_amount?: number
  pickup_time?: string
}

export interface PaginatedResponse<T> {
  data: T[]
  total: number
  success: boolean
}

export interface ReservationFilters {
  user_id?: string
  merchant_id?: string
  status?: string
  basket_id?: string
  product_id?: string
}

// Use proxy configuration from vite.config.ts
const API_BASE_URL = '/api'

const reservationApi = axios.create({
  baseURL: `${API_BASE_URL}/reservations`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add request interceptor for authentication
reservationApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Add response interceptor for error handling
reservationApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('access_token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export const reservationService = {
  // Get all reservations with optional filters
  async getAll(filters: ReservationFilters = {}): Promise<PaginatedResponse<Reservation>> {
    const params = new URLSearchParams()
    
    if (filters.user_id) params.append('user_id', filters.user_id)
    if (filters.merchant_id) params.append('merchant_id', filters.merchant_id)
    if (filters.status) params.append('status', filters.status)
    if (filters.basket_id) params.append('basket_id', filters.basket_id)
    if (filters.product_id) params.append('product_id', filters.product_id)

    const response = await reservationApi.get('', { params })
    return response.data
  },

  // Get reservation by ID
  async getById(id: string): Promise<{ success: boolean; data: Reservation }> {
    const response = await reservationApi.get(`/${id}`)
    return response.data
  },

  // Create new reservation
  async create(reservationData: CreateReservationDto): Promise<{ success: boolean; data: Reservation; message: string }> {
    const response = await reservationApi.post('', reservationData)
    return response.data
  },

  // Update reservation
  async update(id: string, reservationData: UpdateReservationDto): Promise<{ success: boolean; data: Reservation; message: string }> {
    const response = await reservationApi.put(`/${id}`, reservationData)
    return response.data
  },

  // Cancel reservation (soft delete)
  async cancel(id: string): Promise<{ success: boolean; message: string }> {
    const response = await reservationApi.delete(`/${id}`)
    return response.data
  },

  // Update reservation status
  async updateStatus(id: string, status: 'pending' | 'confirmed' | 'cancelled' | 'completed'): Promise<{ success: boolean; data: Reservation; message: string }> {
    const response = await reservationApi.patch(`/${id}/status`, { status })
    return response.data
  },

  // Get QR code for reservation
  async getQrCode(id: string): Promise<{ success: boolean; data: { qr_code_data: string; qr_code_expires_at: string; reservation_id: string; status: string } }> {
    const response = await reservationApi.get(`/${id}/qr-code`)
    return response.data
  },

  // Get reservation statistics
  async getStatistics(): Promise<{
    total: number
    pending: number
    confirmed: number
    cancelled: number
    completed: number
    totalRevenue: number
  }> {
    // This endpoint doesn't exist yet, but we can calculate from all reservations
    const allReservations = await this.getAll()
    const reservations = allReservations.data
    
    const stats = {
      total: reservations.length,
      pending: reservations.filter(r => r.status === 'pending').length,
      confirmed: reservations.filter(r => r.status === 'confirmed').length,
      cancelled: reservations.filter(r => r.status === 'cancelled').length,
      completed: reservations.filter(r => r.status === 'completed').length,
      totalRevenue: reservations.reduce((sum, r) => {
        const amount = typeof r.total_amount === 'string' ? parseFloat(r.total_amount) : r.total_amount
        return sum + amount
      }, 0)
    }
    
    return stats
  }
}

export default reservationService
